import { useEffect, useMemo } from "react";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { MapMarker } from "@/components/map/MapMarker";
import { SportCourtMarker } from "@/components/map/SportCourtMarker";
import { SearchRadiusOverlay } from "@/components/map/SearchRadiusOverlay";
import { getEffectiveRadius } from "@/lib/sport-defaults";
import { statusColor } from "@/lib/mapping-status-colors";

export interface PILMapItem {
  id: string;
  lat: number;
  lng: number;
  ordinal: number;
  status: string;
}

export interface SportCourt {
  id: string;
  lat: number;
  lng: number;
  sport_type: string;
  search_radius_m: number | null;
}

interface PILMapPaneProps {
  location: {
    id: string;
    lat: number;
    lng: number;
    name: string;
    primary_type: string | null;
  };
  pils: PILMapItem[];
  courts: SportCourt[];
  selectedPilId: string | null;
  onSelectPil: (pilId: string) => void;
}

function PILReviewMarker({
  pil,
  isSelected,
  onClick,
}: {
  pil: PILMapItem;
  isSelected: boolean;
  onClick: (pilId: string) => void;
}) {
  const color = statusColor(pil.status);
  const size = isSelected ? 34 : 26;
  return (
    <AdvancedMarker
      position={{ lat: pil.lat, lng: pil.lng }}
      onClick={() => onClick(pil.id)}
      zIndex={isSelected ? 1300 : 1200}
    >
      <div
        className="flex items-center justify-center rounded-full border-2 border-white shadow-lg cursor-pointer transition-transform hover:scale-110"
        style={{
          width: size,
          height: size,
          backgroundColor: color,
          outline: isSelected ? `3px solid ${color}88` : "none",
          outlineOffset: 2,
        }}
      >
        <MapPin size={size * 0.55} color="white" strokeWidth={2.5} />
        <span
          className="absolute -bottom-4 bg-background text-foreground border rounded px-1 text-[10px] font-semibold leading-none py-0.5"
          style={{ pointerEvents: "none" }}
        >
          {pil.ordinal}
        </span>
      </div>
    </AdvancedMarker>
  );
}

function FitBounds({
  location,
  pils,
  selectedPilId,
}: {
  location: { lat: number; lng: number };
  pils: PILMapItem[];
  selectedPilId: string | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || pils.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    for (const pil of pils) bounds.extend({ lat: pil.lat, lng: pil.lng });
    bounds.extend({ lat: location.lat, lng: location.lng });
    map.fitBounds(bounds, 80);
    const zoom = map.getZoom() ?? 0;
    if (zoom > 21) map.setZoom(21);
  }, [map, pils, location.lat, location.lng]);

  useEffect(() => {
    if (!map || !selectedPilId) return;
    const pil = pils.find((p) => p.id === selectedPilId);
    if (!pil) return;
    map.panTo({ lat: pil.lat, lng: pil.lng });
    const z = map.getZoom() ?? 0;
    if (z < 20) map.setZoom(20);
  }, [map, selectedPilId, pils]);

  return null;
}

export function PILMapPane({
  location,
  pils,
  courts,
  selectedPilId,
  onSelectPil,
}: PILMapPaneProps) {
  const radiusCourts = useMemo(
    () =>
      courts.map((c) => ({
        id: c.id,
        lat: c.lat,
        lng: c.lng,
        radius: getEffectiveRadius(c.sport_type, c.search_radius_m),
      })),
    [courts],
  );

  return (
    <GoogleMapWrapper
      key={location.id}
      defaultCenter={{ lat: location.lat, lng: location.lng }}
      defaultZoom={19}
      mapTypeId="hybrid"
      fullscreenControl={false}
    >
      <FitBounds location={location} pils={pils} selectedPilId={selectedPilId} />
      <MapMarker
        locationId={location.id}
        position={{ lat: location.lat, lng: location.lng }}
        hasSportField={null}
        primaryType={location.primary_type}
        name={location.name}
        isMuted
        isReviewed={false}
        isRejected={false}
      />
      <SearchRadiusOverlay courts={radiusCourts} />
      {courts.map((court) => (
        <SportCourtMarker
          key={court.id}
          courtId={court.id}
          position={{ lat: court.lat, lng: court.lng }}
          sportType={court.sport_type}
        />
      ))}
      {pils.map((pil) => (
        <PILReviewMarker
          key={pil.id}
          pil={pil}
          isSelected={pil.id === selectedPilId}
          onClick={onSelectPil}
        />
      ))}
    </GoogleMapWrapper>
  );
}
